import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import useGamesList from '@hooks/API/useGamesList';
import getMenuGames from '@helpers/getMenuGames';
import { useFeature } from 'flagged';
import { useLocalAppContext } from '@context/LocalAppContext';
const useStyles = makeStyles()((theme) => ({
    wrapper: {
        position: 'relative',
        backgroundColor: theme.palette.common.black,
        color: theme.palette.grey[400],
        padding: theme.spacing(4),
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(8),
        },
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        fontSize: '14px',
        margin: '0 auto',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            maxWidth: 1420,
        },
    },
    brandingSection: {
        color: theme.palette.common.white,
        textAlign: 'center',
        [theme.breakpoints.up('md')]: {
            textAlign: 'left',
        },
    },
    logo: {
        height: 40,
        paddingRight: theme.spacing(4),
    },
    socialLinks: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        listStyle: 'none',
        margin: 0,
        padding: theme.spacing(2),
        '& a:link, & a:visited, & a:active, & a:hover, & a:focus': {
            display: 'block',
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('md')]: {
            display: 'inline-flex',
            padding: 0,
        },
    },
    tagline: {
        padding: theme.spacing(1, 0, 2),
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(3, 0, 1),
        },
    },
    copyright: {
        color: theme.palette.grey[400],
        fontSize: '12px',
        margin: 0,
        padding: theme.spacing(1, 0),
    },
    linkSection: {
        display: 'flex',
        flexWrap: 'wrap',
        paddingTop: theme.spacing(4),
        [theme.breakpoints.up('md')]: {
            flexWrap: 'nowrap',
            justifyContent: 'end',
            paddingTop: 0,
        },
    },
    linkListWrapper: {
        flexBasis: '50%',
        [theme.breakpoints.up('md')]: {
            flexBasis: 'unset',
            paddingLeft: theme.spacing(8),
        },
    },
    linkListHeading: {
        color: theme.palette.common.white,
        fontSize: '16px',
    },
    linkList: {
        listStyle: 'none',
        paddingLeft: 0,
        '& a:link, & a:visited': {
            color: theme.palette.grey[400],
            display: 'block',
            padding: theme.spacing(1, 0),
        },
        '& a:active, & a:hover, & a:focus': {
            color: theme.palette.common.white,
        },
    },
    disclaimer: {
        backgroundColor: theme.palette.grey[900],
        color: theme.palette.grey[400],
        padding: theme.spacing(4),
        position: 'relative',
    },
    disclaimerContent: {
        margin: '0 auto',
        textAlign: 'center',
        maxWidth: (1420 / 12) * 8,
        fontSize: '14px',
    },
    logoAndSocial: {
        [theme.breakpoints.up('md')]: {
            display: 'flex',
            alignItems: 'center',
        },
    },
}));
const PageFooter = () => {
    const currentYear = new Date().getFullYear();
    const { classes } = useStyles();
    const { games } = useGamesList();
    const menuGames = getMenuGames(games);
    const isWithdrawalOnlyEnabled = !!useFeature('semisonic_phase_2');
    const { openModal } = useLocalAppContext();
    const handleSigninClick = () => {
        openModal({
            type: 'login',
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx("footer", { className: classes.wrapper, children: _jsxs("div", { className: classes.container, children: [_jsxs("div", { className: classes.brandingSection, children: [_jsxs("div", { className: classes.logoAndSocial, children: [_jsx("img", { className: classes.logo, src: "/img/logos/repeat_full_logo_white_suffix.svg", alt: "" }), _jsx("ul", { className: classes.socialLinks, children: !isWithdrawalOnlyEnabled && (_jsxs(_Fragment, { children: [_jsx("li", { children: _jsx("a", { href: "https://www.youtube.com/channel/UCSzCC0eFKw-rOVycmxsXhUg", target: "_blank", rel: "noopener noreferrer", children: _jsx("img", { src: "/img/landing/social-youtube.png", alt: "" }) }) }), _jsx("li", { children: _jsx("a", { href: "https://twitter.com/Repeatgg", target: "_blank", rel: "noopener noreferrer", children: _jsx("img", { src: "/img/landing/social-twitter.png", alt: "" }) }) }), _jsx("li", { children: _jsx("a", { href: "https://www.twitch.tv/repeat_gg", target: "_blank", rel: "noopener noreferrer", children: _jsx("img", { src: "/img/landing/social-twitch.png", alt: "" }) }) })] })) })] }), _jsxs("p", { className: classes.tagline, children: ["Play the games you love. ", _jsx("br", {}), " Compete in free tournaments.", _jsx("br", {}), "Win real money & prizes."] }), _jsxs("p", { className: classes.copyright, children: [_jsx("span", { "data-ignore": true, children: "Repeat" }), " ", currentYear, " All Rights Reserved."] })] }), _jsxs("div", { className: classes.linkSection, children: [!isWithdrawalOnlyEnabled && (_jsxs(_Fragment, { children: [_jsxs("div", { className: classes.linkListWrapper, children: [_jsx("h4", { className: classes.linkListHeading, children: "Contact" }), _jsxs("ul", { className: classes.linkList, children: [_jsx("li", { children: _jsx("a", { href: "mailto:contact@repeat.gg?subject=Contact Repeat", children: "Influencers" }) }), _jsx("li", { children: _jsx("a", { href: "mailto:contact@repeat.gg?subject=Contact Repeat", children: "Communities" }) }), _jsx("li", { children: _jsx("a", { href: "mailto:contact@repeat.gg?subject=Contact Repeat", children: "Developers" }) }), _jsx("li", { children: _jsx("a", { href: "mailto:contact@repeat.gg?subject=Contact Repeat", children: "Brands" }) })] })] }), _jsxs("div", { className: classes.linkListWrapper, children: [_jsx("h4", { className: classes.linkListHeading, children: "Platform" }), _jsxs("ul", { className: classes.linkList, children: [_jsx("li", { children: _jsx(Link, { to: "/tournaments", children: "Tournaments" }) }), _jsx("li", { children: _jsx(Link, { to: "/marketplace", children: "Marketplace" }) }), menuGames.map((game) => (_jsx(Link, { to: `/${game.slug}`, children: game.name }, game.id)))] })] }), _jsxs("div", { className: classes.linkListWrapper, children: [_jsx("h4", { className: classes.linkListHeading, children: "Company" }), _jsxs("ul", { className: classes.linkList, children: [_jsx("li", { children: _jsx("a", { href: "https://support.repeat.gg/hc/en-us", target: "_blank", children: "Knowledge Base" }) }), _jsx("li", { children: _jsx("a", { href: "/content", children: "News & Blog" }) }), _jsx("li", { children: _jsx("a", { href: "https://www.linkedin.com/company/repeatgg/", target: "_blank", rel: "noopener noreferrer", children: "Linkedin" }) })] })] })] })), _jsxs("div", { className: classes.linkListWrapper, children: [_jsx("h4", { className: classes.linkListHeading, children: isWithdrawalOnlyEnabled ? 'Other' : 'Legal' }), _jsxs("ul", { className: classes.linkList, children: [_jsx("li", { children: _jsx("a", { href: "/about/terms-and-conditions", children: "Terms of Use" }) }), _jsx("li", { children: _jsx("a", { href: "/about/privacy-policy", children: "Privacy Policy" }) }), isWithdrawalOnlyEnabled && (_jsx("li", { children: _jsx("a", { href: "#", onClick: handleSigninClick, children: "Login" }) }))] })] })] })] }) }), _jsx("div", { className: classes.disclaimer, children: _jsxs("p", { className: classes.disclaimerContent, children: [_jsx("span", { "data-ignore": true, children: "Repeat.gg" }), " is not endorsed by, directly affiliated with, maintained or sponsored by", ' ', _jsx("span", { "data-ignore": true, children: "Apple Inc" }), ", ", _jsx("span", { "data-ignore": true, children: "Electronic Arts" }), ",", ' ', _jsx("span", { "data-ignore": true, children: "Activision Blizzard" }), ", ", _jsx("span", { "data-ignore": true, children: "Take-Two Interactive" }), ",", ' ', _jsx("span", { "data-ignore": true, children: "Riot Games" }), ", ", _jsx("span", { "data-ignore": true, children: "Microsoft" }), ", ", _jsx("span", { "data-ignore": true, children: "Xbox" }), " or", ' ', _jsx("span", { "data-ignore": true, children: "Epic Games" }), ". All content, games titles, trade names and/or trade dress, trademarks, artwork and associated imagery are trademarks and/or copyright material of their respective owners. Repeat is a trademark of Sony Interactive Entertainment LLC."] }) })] }));
};
export default PageFooter;
